<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/cn';

type BadgeVariant =
  | 'critical' | 'high' | 'medium' | 'low' | 'resolved'
  | 'new' | 'progress' | 'info'
  | 'ai'
  | 'pending' | 'approved' | 'denied' | 'verified'
  | 'soft';

const badge = cva(
  'inline-flex items-center gap-1 px-2 h-[22px] text-caption font-medium rounded-pill border whitespace-nowrap',
  {
    variants: {
      variant: {
        // Severity (per brief §4 — fixed mapping; do not change)
        critical: 'bg-red-100 text-red-700 border-red-100',
        high:     'bg-orange-100 text-orange-600 border-orange-100',
        medium:   'bg-amber-100 text-amber-700 border-amber-100',
        low:      'bg-navy-100 text-navy-700 border-navy-100',
        resolved: 'bg-green-100 text-green-700 border-green-100',

        // Status
        new:      'bg-violet-100 text-violet-600 border-violet-100',
        progress: 'bg-amber-100 text-amber-700 border-amber-100',
        info:     'bg-canvas-soft text-fg-soft border-border',

        // AI — teal, per brief §12 (AI signal = teal, used exclusively for AI content)
        ai:       'bg-teal-100 text-teal-700 border-teal-100',

        // Approval lifecycle
        pending:  'bg-amber-100 text-amber-700 border-amber-100',
        approved: 'bg-green-100 text-green-700 border-green-100',
        denied:   'bg-red-100 text-red-700 border-red-100',
        verified: 'bg-green-100 text-green-700 border-green-100',

        // Generic soft chip
        soft:     'bg-canvas-soft text-muted border-border',
      },
    },
    defaultVariants: { variant: 'info' },
  },
);

interface Props {
  variant?: BadgeVariant;
  noDot?: boolean;
  class?: string;
}

const props = defineProps<Props>();

// Dot color mirrors the badge's foreground for visual coherence; opt out with noDot.
const dotClass: Record<BadgeVariant, string> = {
  critical: 'bg-red-600',
  high:     'bg-orange-600',
  medium:   'bg-amber-700',
  low:      'bg-navy-500',
  resolved: 'bg-green-600',
  new:      'bg-violet-600',
  progress: 'bg-amber-700',
  info:     'bg-muted',
  ai:       'bg-teal-600',
  pending:  'bg-amber-700',
  approved: 'bg-green-600',
  denied:   'bg-red-600',
  verified: 'bg-green-600',
  soft:     'bg-muted',
};
</script>

<template>
  <span :class="cn(badge({ variant }), props.class)">
    <span
      v-if="!noDot && variant"
      :class="cn('inline-block w-1.5 h-1.5 rounded-pill', dotClass[variant])"
      aria-hidden="true"
    />
    <slot />
  </span>
</template>
